import clsx from 'clsx';
import React, { useContext, useEffect, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { createUseStyles } from 'react-jss';
import { useHistory } from 'react-router';
import { WHITE } from '../../backend/helpers/Theme';
import mode from '../../shared/helpers/Mode';
import Url from '../../shared/helpers/Url';
import UTM from '../../shared/helpers/UtmParams';
import { MEETING_METHODS } from '../constants';
import { FeatureContext } from '../contexts/FeatureContext';
import { LocaleContext } from '../contexts/LocaleContext';
import { SelectionContext } from '../contexts/SelectionContext';
import { SettingsContext } from '../contexts/SettingsContext';
import { StepContext } from '../contexts/StepContext';
import Api from '../helpers/Api';
import Resources from '../helpers/Resources';
import { CalendarIcon, CheckInIcon, WalkInIcon } from './KioskIcons';

const getBackgroundColor = (theme) => {
  return theme.palette.contrastText === WHITE
    ? 'rgba(255, 255, 255, 0.2)'
    : 'rgba(0, 0, 0, 0.15)';
};

const getBorderColor = (theme) => {
  return theme.palette.contrastText === WHITE
    ? 'rgba(255, 255, 255, 0.5)'
    : 'rgba(0, 0, 0, 0.5)';
};

const useStyles = createUseStyles((theme: any) => ({
  subtitle: {
    fontSize: '2.25rem',
    '@media (min-width: 640px)': {
      fontSize: '2.75rem',
    },
  },
  choice: {
    animation: '$zoomFadeIn 0.4s ease-out forwards',
    backgroundColor: 'rgba(255, 255, 255, 0.045)',
    border: `2px solid ${getBorderColor(theme)}`,
    opacity: 0,
    transform: 'scale(0.8)',
  },
  bgIcon: {
    backgroundColor: getBackgroundColor(theme),
  },
  '@keyframes zoomFadeIn': {
    from: {
      opacity: 0,
      transform: 'scale(0.8)',
    },
    to: {
      opacity: 1,
      transform: 'scale(1)',
    },
  },
  choices: {
    animationDelay: '0.3s',
    maxHeight: 'fit-content',
    height: '100%',
    '@media (orientation: portrait) and (min-width: 768px)': {
      maxHeight: '18rem',
      height: '18rem',
      alignItems: 'end',
    },
    overflow: 'visible',
  },
}));

interface KioskLandingChoicesProps {
  className: string;
}

export const KioskLandingChoices = ({
  className,
}: KioskLandingChoicesProps) => {
  const intl = useIntl();
  const classes = useStyles();
  const history = useHistory();
  const [locale] = useContext(LocaleContext);
  const features = useContext(FeatureContext);
  const { kioskAppointmentBooking } = useContext(SettingsContext);
  const [, , remapSteps, resetInitialSteps] = useContext(StepContext) as any;
  const [{ location, meetingMethod, service }, setSelections] = useContext(
    SelectionContext,
  ) as any;

  const method = meetingMethod || MEETING_METHODS.AT_LOCATION;
  const language = `&lang=${locale}`;
  const search = `?meeting_method=${method}${language}`;

  const [state, setState] = useState(() => ({
    loading: true,
    queueable: true,
  }));

  const options = getOptions({
    history,
    location,
    queueable: state.queueable,
    remapSteps,
    search,
    service: service?.id,
    setSelections,
    showCheckInOption:
      !mode.isInvitation() && features.appointmentBookingChannel,
    showAppointmentOption:
      features.appointmentBookingChannel && kioskAppointmentBooking,
    shouldUseKioskEnhancementsRelease:
      features.shouldUseKioskEnhancementsRelease,
  });

  useEffect(() => {
    resetInitialSteps();

    if (service?.id && location?.id) {
      Api.lobbyAvailability()
        .all({
          location: location.id,
          service: service.id,
          meetingMethod: method,
        })
        .then((res) => {
          setState({
            loading: false,
            queueable: res.available && features.lobby,
          });
        });
    } else {
      Api.locations()
        .queueable(location.id)
        .then((res) => {
          setState({
            loading: false,
            queueable: res.queueable && features.lobby,
          });
        });
    }
  }, [location.id]);

  return (
    <section
      aria-label={intl.formatMessage({ id: 'Landing.kiosk.options' })}
      className={className}
    >
      <div
        className={clsx(
          'w-full grid grid-rows-3 gap-4 sm:gap-6 md:grid-rows-1 justify-items-center',
          options.length === 3 && 'md:grid-cols-3',
          options.length === 2 && 'md:grid-cols-2',
          options.length === 1 && 'md:grid-cols-1',
          classes.choices,
        )}
      >
        {options.map((option, index) => (
          <OptionCard key={option.id} {...option} index={index} />
        ))}
        {options.length === 0 && (
          <div className="col-span-3 flex items-center justify-center h-48 text-2xl flex-col gap-4">
            <FormattedMessage id="Landing.kiosk.no_options" tagName="p" />
            <FormattedMessage id="Landing.kiosk.speak_to_staff" tagName="p" />
          </div>
        )}
      </div>
    </section>
  );
};

type Option = {
  id: string;
  icon: React.JSX.Element;
  action: () => void;
  primary: string;
  secondary: string;
  index?: number;
};

const OptionCard = ({
  id,
  icon,
  action,
  primary,
  secondary,
  index = 0,
}: Option) => {
  const classes = useStyles();

  return (
    <button
      className={clsx(
        classes.choice,
        'rounded-3xl row-span-1 md:row-auto md:col-span-1 h-full md:h-72 flex md:flex-col gap-6 p-4 sm:p-5 md:py-7 md:px-4 items-center md:justify-center md:max-w-66 w-full',
      )}
      id={id}
      key={id}
      onClick={action}
      style={{ animationDelay: `${index * 0.15}s` }}
      type="button"
    >
      <div className={clsx(classes.bgIcon, 'p-3 rounded-full w-fit')}>
        {icon}
      </div>
      <div className="flex flex-col items-start gap-2 md:gap-6 md:items-center md:h-full text-left sm:text-center">
        <h3 className="text-xl sm:text-2xl font-semibold md:h-12 sm:flex sm:items-center md:px-1">
          <FormattedMessage id={primary} />
        </h3>
        <h4 className="text-base sm:text-lg opacity-90 leading-5">
          <FormattedMessage id={secondary} />
        </h4>
      </div>
    </button>
  );
};

const getOptions = ({
  history,
  location,
  queueable,
  remapSteps,
  search,
  service,
  setSelections,
  showCheckInOption,
  showAppointmentOption,
  shouldUseKioskEnhancementsRelease = false,
}) => {
  const params = Url.params(history.location.search);
  const utmParams = UTM.getUtmParams(params, '&');
  const originParams = `&${mode.kioskParam()}`;
  const bookingThroughParam = mode.getBookedThroughKioskQRParam();
  const serviceParam = service ? `&service=${service}` : '';
  const workflowParams = params.workflow ? `&workflow=${params.workflow}` : '';
  const useNewKioskParam = shouldUseKioskEnhancementsRelease
    ? ''
    : mode.useNewKioskParam('&');
  const checkInUrl = `/check-in/${location.id}${search}${utmParams}${originParams}${workflowParams}${useNewKioskParam}${bookingThroughParam}`;

  const options: Option[] = [];

  if (queueable) {
    options.push({
      id: mode.walkInParam(),
      icon: <WalkInIcon />,
      action: () => {
        const newSearch = `${window.location.search}${mode.walkInParam('&')}`;
        setSelections({ bookingWalkIn: true, search: newSearch });
        history.push({ pathname: '/', search: newSearch });
      },
      primary: 'Landing.kiosk.walk_in.title',
      secondary: 'Landing.kiosk.walk_in.subtitle',
    });
  }

  if (showCheckInOption) {
    options.push({
      id: 'check_in',
      icon: <CheckInIcon />,
      action: () => {
        history.push(checkInUrl);
      },
      primary: 'Landing.kiosk.check_in.title',
      secondary: 'Landing.kiosk.check_in.subtitle',
    });
  }

  if (showAppointmentOption) {
    options.push({
      id: 'book_appointment',
      icon: <CalendarIcon />,
      action: () => {
        (window as any).state.shortcuts.location =
          Resources.unformatLocation(location);
        remapSteps();
        history.push({
          pathname: '/',
          search: `?location=${location.id}${serviceParam}${utmParams}${originParams}${workflowParams}${useNewKioskParam}${bookingThroughParam}`,
        });
      },
      primary: 'Landing.kiosk.appointment.title',
      secondary: 'Landing.kiosk.appointment.subtitle',
    });
  }

  return options;
};
